import React, { useMemo } from 'react'
import { localizedStrings } from '@core/strings'
import {
  useGetRegisterItemCompletedInspections,
  useGetRegisterItemTasks,
} from '@core/react-query/features/registers/registers/register/items/item'
import { NavigationAwareTabs } from '@elements'
import { Box, Flex, Text } from '@fundamentals'
import { LinkedDocumentsList } from '@elements/LinkingProcesses/LinkedDocumentsList'
import LinkIcon from '@mui/icons-material/Link'
import { useNavigationContext } from '@common/hooks/useNavigationContext'
import { RegisterInspectionCardDetailRow } from './RegisterInspectionCardDetailRow'
import { ScheduledInspection } from '@core/entities/register/RegisterInspection/ScheduledInspection'
import moment from 'moment'
import { Divider } from '@mui/material'
import { Badge, Button, LinearProgress } from '@common/material'
import { PlayCircleOutline } from '@mui/icons-material'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { encodeReturnUrl } from 'webapp/utils/returnUrl'
import type { InspectionSidePanelTab } from './RegisterScheduleInspectionsCard'
import { RegisterInspection } from '@core/entities/register/RegisterInspection'
import { useDrawer } from '@common/GlobalDrawer'

type RegisterItemInspectionTabsProps = {
  stickyPosition?: number | string
  registerId: number
  registerItemId: number
  projectId?: number
  initialTab?: InspectionSidePanelTab
  scheduledInspectionId?: number
  scheduledInspection: ScheduledInspection
  inspection: RegisterInspection
}

export const RegisterInspectionDetailsTabs: React.FC<
  RegisterItemInspectionTabsProps
> = ({
  stickyPosition = 0,
  registerId,
  registerItemId,
  projectId,
  initialTab,
  scheduledInspectionId,
  scheduledInspection,
  inspection,
}) => {
  const { organisationId } = useNavigationContext()
  const { hideDrawer } = useDrawer()
  const router = useRouter()
  const {
    data: linkedInspections = [],
    isInitialLoading: isLoadingLinkedInspections,
  } = useGetRegisterItemCompletedInspections(
    {
      registerTemplateInspectionId:
        scheduledInspection?.registerTemplateInspectionId ||
        inspection?.registerTemplateInspectionId,
      registerId,
      registerItemId,
      scheduledInspectionId,
      page: 1,
      size: 1000,
    },
    {
      enabled: !!(registerId && registerItemId),
    },
  )
  const { data: linkedTasks = [], isInitialLoading: isLoadingLinkedTasks } =
    useGetRegisterItemTasks(
      {
        registerTemplateInspectionId:
          scheduledInspection?.registerTemplateInspectionId ||
          inspection?.registerTemplateInspectionId,
        registerId,
        registerItemId,
        scheduledInspectionId,
        page: 1,
        size: 1000,
      },
      {
        enabled: !!(registerId && registerItemId),
      },
    )
  const isLoadingLinkedData = isLoadingLinkedInspections || isLoadingLinkedTasks
  const tabsIndexes = {
    overview: 0,
    inspections: 1,
    tasks: 2,
  }

  const initialTabValue = useMemo(() => {
    // redirect user to overview tab when no linked data is found
    if (initialTab === 'tasks' && !linkedTasks.length) return 'overview'
    if (initialTab === 'inspections' && !linkedInspections.length)
      return 'overview'
    return initialTab
  }, [initialTab, linkedTasks, linkedInspections])

  const nextDueText = moment(scheduledInspection?.nextInspectionDue).format(
    'DD MMM YYYY HH:mm',
  )

  const isOverdue = scheduledInspection?.isOverdue()

  const canSubmitInspection =
    !!projectId && scheduledInspection?.canSubmitInspection

  const runInspection = () => {
    const returnUrl = encodeReturnUrl({
      pathname: router.pathname,
      query: router.query,
    })

    router.push({
      pathname: PageUrls.newChecklistProject({
        organisationId,
        projectId,
      }),
      query: {
        registerId,
        registerItemId,
        projectId,
        registerTemplateInspectionId:
          scheduledInspection.registerTemplateInspectionId,
        inspectionTemplateId:
          scheduledInspection.scheduleConfig?.checklistTemplateId,
        scheduledInspectionId: scheduledInspection.id,
        returnUrl,
        isUsedForInspections: true,
      },
    })
    hideDrawer()
  }

  const tabs = useMemo(() => {
    const tabBuilder = NavigationAwareTabs.TabBuilder()
    tabBuilder.addTab({
      name: 'overview',
      label: localizedStrings.overview,
      component: (
        <Flex flexDirection='column' gap={1.5} sx={{ py: 2 }}>
          <RegisterInspectionCardDetailRow
            label={localizedStrings.assignee}
            value={scheduledInspection?.assignedTo.getFullName()}
          />
          <RegisterInspectionCardDetailRow
            label={localizedStrings.schedule}
            value={scheduledInspection?.getScheduleDisplayLabel()}
          />
          <RegisterInspectionCardDetailRow
            label={localizedStrings.nextDue}
            value={
              <Text variant='body2' color={isOverdue ? 'error' : undefined}>
                {nextDueText}
              </Text>
            }
          />
          <RegisterInspectionCardDetailRow
            label={localizedStrings.openTasks}
            value={
              <Badge
                count={scheduledInspection?.openTasks ?? 0}
                hideZero={false}
              />
            }
          />
          <Divider sx={{ my: 0 }} />
          <RegisterInspectionCardDetailRow
            label={localizedStrings.completedRuns}
            value={
              <Badge
                count={scheduledInspection?.completedRuns ?? 0}
                hideZero={false}
              />
            }
          />
          {canSubmitInspection ? (
            <Button
              variant='text'
              color='primary'
              startIcon={<PlayCircleOutline sx={{ fontSize: 20 }} />}
              onClick={runInspection}
              sx={{
                alignSelf: 'flex-start',
              }}
            >
              {localizedStrings.runInspection}
            </Button>
          ) : null}
        </Flex>
      ),
    })
    if (linkedInspections.length) {
      tabBuilder.addTab({
        name: 'inspections',
        icon: <LinkIcon />,
        label: localizedStrings.inspections,
        badgeCount: linkedInspections.length,
        component: (
          <LinkedDocumentsList
            items={linkedInspections}
            organisationId={organisationId}
          />
        ),
      })
    }
    if (linkedTasks.length) {
      tabBuilder.addTab({
        name: 'tasks',
        icon: <LinkIcon />,
        label: localizedStrings.tasks,
        badgeCount: linkedTasks.length,
        component: (
          <LinkedDocumentsList
            items={linkedTasks}
            organisationId={organisationId}
          />
        ),
      })
    }
    return tabBuilder.build()
  }, [linkedTasks, linkedInspections])

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoadingLinkedData && (
        <Box
          sx={{
            position: 'absolute',
            top: 55,
            left: 0,
            right: 0,
            zIndex: 21,
          }}
        >
          <LinearProgress />
        </Box>
      )}
      <NavigationAwareTabs
        tabs={tabs}
        withoutQueryParam
        initialTab={tabsIndexes[initialTabValue]}
        tabHeaderSx={{
          position: 'sticky',
          top: stickyPosition,
          zIndex: 20,
          bgcolor: 'background.paper',
        }}
      />
    </Box>
  )
}
